import { Cross1Icon, GearIcon } from "@radix-ui/react-icons";
import { ArrowDownAzIcon, ListTodoIcon } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { useFocusWithin } from "react-aria";
import { flushSync } from "react-dom";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  NodeBlockHeader,
  BlockContainerShapeContext,
  BlockSeparator,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasButton,
  CanvasInput,
  CanvasSimpleSelect,
  CanvasTextArea,
  CanvasToggle,
  FieldSelect,
} from "@/components/canvas-editor/components/Inputs";
import { NodeDocs } from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  Dimensions,
  Field,
  fields,
  MultiRegexProps,
  MultiRegexShape,
  multiRegexType,
  RegexMatch,
  metadataType,
  textOperators,
} from "@/components/canvas-editor/shapes/shapes";
import {
  preventBubble,
  ToolStateNode,
  useIsReadonlyEditor,
} from "@/components/canvas-editor/utils";
import { ListWrapper, ReadOnlyValue } from "@/components/hooks/ListContainer";
import { Kbd, KbdGroup } from "@/components/ui/kbd";
import {
  DropdownMenu,
  MenuCheckboxItem,
  MenuRadioGroup,
  MenuRadioItem,
  MenuContent,
  MenuSeparator,
  MenuLabel,
  MenuTrigger,
  MenuItem,
} from "@/components/ui/menu";
import { SimpleTooltip } from "@/components/ui/tooltip";
import {
  asRegexTerms,
  inertTerms,
  uncompilableTerms,
} from "@/lib/literal-terms";
import {
  BOUNDARY_HELP,
  BOUNDARY_LABEL,
  BOUNDARY_SEPARATOR,
  termBoundary,
} from "@/lib/term-boundary";

const ROW_HEIGHT = 48;

export function isMultiRegexShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is MultiRegexShape {
  return (
    shape.type === multiRegexType &&
    ["any", "none"].includes((shape.props as MultiRegexProps).type)
  );
}

export class MultiRegexTool extends ToolStateNode {
  static override id = multiRegexType;

  static override uiToolDef = (editor: Editor) => ({
    id: multiRegexType,
    icon: ListTodoIcon,
    label: MultiRegexShapeUtil.docs.title,
    defaultColor: MultiRegexShapeUtil.docs.defaultColor,
    kbd: "t",
    onSelect: () => editor.setCurrentTool(multiRegexType),
    docs: () => (
      <NodeDocs
        editor={editor}
        component={MultiRegex}
        util={MultiRegexShapeUtil}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: multiRegexType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class MultiRegexShapeUtil extends BindableUtil<MultiRegexShape> {
  static override migrations = createMigrations(multiRegexType);
  static override type = multiRegexType;

  static override docs = {
    defaultColor: "green" as const,
    title: "Word List",
    description:
      "Match/exclude a post based on a list of words. If any of the words match, the filter is active.",
  };

  override getType() {
    return multiRegexType;
  }

  static override props: RecordProps<MultiRegexShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    // @ts-expect-error We want users to be able to select any field
    var: T.string,
    regex: T.arrayOf(T.string),
    type: T.literalEnum("any", "none"),
    insensitive: T.boolean.optional(),
    regex_match: T.boolean.optional(),
    separator: T.string.optional(),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 350,
      minW: 350,
      h: 184,
      var: fields[0],
      regex: ["alf"],
      type: "any" as const,
      insensitive: true,
      regex_match: false,
      metadata: undefined,
    };
  }

  override onBeforeCreate(next: MultiRegexShape): void | MultiRegexShape {
    const { regex } = next.props;

    if (regex.length > 1) {
      next.props.h += (regex.length - 1) * ROW_HEIGHT;
    }
  }

  override component(shape: MultiRegexShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <MultiRegex
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

/**
 * Say when a term in this list cannot match anything.
 *
 * With "Regex Match" off the engine escapes every term, so `foot*` looks for an
 * actual asterisk and `\bNFL\b` looks for an actual backslash. Both are stored,
 * compiled and run against every post, and neither can ever be true -- and
 * nothing in the editor said so. That is how feed 34972 went quiet: a keyword
 * node carrying eleven wildcard terms, only the plain words doing any work.
 *
 * The one-click fix rewrites the whole list rather than just flipping the
 * toggle, because the toggle reinterprets the terms the author did NOT write as
 * patterns -- `Mother f*cker` read as a regex is "Mother f, zero or more times,
 * cker". `asRegexTerms` escapes those and expands only the wildcards, so the
 * node keeps meaning exactly what it means today apart from the part that was
 * broken. See `src/lib/literal-terms.ts`.
 *
 * Not blocked, and not a dialog: the list still works, minus these terms, and
 * an author who typed `f*ck` on purpose is not making a mistake.
 */
function InertTermsNotice({
  shape,
  updateShape,
}: {
  shape: MultiRegexShape;
  updateShape: (
    shape: MultiRegexShape,
    props: Partial<MultiRegexProps>,
  ) => void;
}) {
  const inert = inertTerms(shape.props.regex, shape.props.regex_match);

  if (inert.length === 0) {
    return null;
  }

  // A term that no engine can compile would turn a node that merely does
  // nothing into one that fails to load, so it is named instead of rewritten.
  const malformed = uncompilableTerms(shape.props.regex);
  const named = inert.slice(0, 3).map((t) => `“${t.term}”`).join(", ");
  const rest = inert.length - Math.min(inert.length, 3);

  return (
    <div className="flex flex-col gap-1 text-xs text-amber-11 dark:text-amberdark-11">
      <span>
        {named}
        {rest > 0 ? ` and ${rest} more` : ""}
        {inert.length === 1 ? " is" : " are"} matched as text, punctuation and
        all, so {inert.length === 1 ? "it" : "they"} can never match a post.
      </span>
      {malformed.length > 0 ? (
        <span>
          Fix {`“${malformed[0]}”`} before switching this node to Regex
          Match — it is not a pattern any engine can read.
        </span>
      ) : (
        <CanvasButton
          size="sm"
          variant="outline"
          className="self-start"
          onPress={() =>
            updateShape(shape, {
              regex: asRegexTerms(shape.props.regex),
              regex_match: true,
            })
          }
        >
          Use Regex Match
        </CanvasButton>
      )}
    </div>
  );
}

function MultiRegex({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: MultiRegexShape;
  className?: string;
  updateShape: (
    shape: MultiRegexShape,
    props: Partial<MultiRegexProps>,
  ) => void;
}) {
  const isReadOnly = useIsReadonlyEditor() || shape.props.isReadOnly;
  const boundary = termBoundary(shape.props.separator);
  const variant =
    shape.props.type === "match" || shape.props.type === "any"
      ? "green"
      : "red";
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );
  const ref = useRef<HTMLDivElement>(null);
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  const { focusWithinProps } = useFocusWithin({
    onFocusWithinChange: setIsFocusWithin,
  });
  const onAddAnother = useCallback(() => {
    // eslint-disable-next-line @eslint-react/dom/no-flush-sync
    flushSync(() => {
      updateShape(shape, { regex: [...shape.props.regex, ""] });
    });

    if (!ref.current) {
      return;
    }

    const inputs = ref.current.querySelectorAll<HTMLInputElement>("input");

    inputs.values().toArray().at(-1)?.focus();
  }, [shape, updateShape]);

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        ref={ref}
        {...focusWithinProps}
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={shape.props.metadata?.color || variant}
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
        onKeyDownCapture={(e) => {
          if (e.key === "Enter" && e.metaKey) {
            e.preventDefault();
            e.stopPropagation();

            onAddAnother();
          }
        }}
      >
        <NodeBlockHeader
          props={shape.props}
          docs={{
            ...MultiRegexShapeUtil.docs,
            title: `${shape.props.insensitive ? "" : "Case Sensitive "}${
              shape.props.regex_match
                ? "Regex List"
                : MultiRegexShapeUtil.docs.title
            }`,
          }}
        />
        <BlockBody>
          <div className="flex-1 flex flex-col gap-2">
            <div className="flex gap-2">
              <div className="flex gap-2 flex-1">
                <DropdownMenu>
                  <SimpleTooltip message="Settings">
                    <MenuTrigger
                      asChild
                      className="pointer-events-auto"
                      {...preventBubble}
                    >
                      <CanvasToggle variant="outline" pressed={false}>
                        <span className="sr-only">Settings</span>
                        <GearIcon />
                      </CanvasToggle>
                    </MenuTrigger>
                  </SimpleTooltip>
                  <MenuContent align="start" {...preventBubble}>
                    <MenuItem
                      onSelect={() => {
                        const sorted = [...shape.props.regex].sort();

                        if (
                          JSON.stringify(sorted) ===
                          JSON.stringify(shape.props.regex)
                        ) {
                          sorted.reverse();
                        }

                        updateShape(shape, {
                          regex: sorted,
                        });
                      }}
                    >
                      <ArrowDownAzIcon />
                      Sort Alphabetically
                    </MenuItem>
                    <MenuSeparator />
                    <MenuLabel>Settings</MenuLabel>
                    <MenuCheckboxItem
                      checked={!shape.props.insensitive}
                      onCheckedChange={(checked) =>
                        updateShape(shape, { insensitive: !checked })
                      }
                    >
                      Case Sensitive
                    </MenuCheckboxItem>
                    <MenuCheckboxItem
                      checked={shape.props.regex_match}
                      onCheckedChange={(checked) =>
                        updateShape(shape, { regex_match: checked })
                      }
                    >
                      Regex Match
                    </MenuCheckboxItem>
                    <MenuSeparator />
                    <MenuLabel>Match terms as</MenuLabel>
                    <MenuRadioGroup
                      value={boundary}
                      onValueChange={(value: string) => {
                        if (value === "word" || value === "anywhere") {
                          updateShape(shape, {
                            separator: BOUNDARY_SEPARATOR[value],
                          });
                        }
                      }}
                    >
                      <MenuRadioItem value="word">
                        {BOUNDARY_LABEL.word}
                      </MenuRadioItem>
                      <MenuRadioItem value="anywhere">
                        {BOUNDARY_LABEL.anywhere}
                      </MenuRadioItem>
                      {boundary === "custom" && (
                        <MenuRadioItem value="custom" disabled>
                          {BOUNDARY_LABEL.custom}
                        </MenuRadioItem>
                      )}
                    </MenuRadioGroup>
                  </MenuContent>
                </DropdownMenu>
                <FieldSelect
                  validOperators={textOperators}
                  className="flex-1"
                  value={shape.props.var}
                  onValueChange={(v) => updateShape(shape, { var: v as Field })}
                />
              </div>
              <div className="flex-1">
                <CanvasSimpleSelect
                  aria-label="Regex Match Type"
                  value={shape.props.type}
                  options={[
                    { value: "any", label: "contains" },
                    { value: "none", label: "missing" },
                  ]}
                  placeholder="Type"
                  className="w-full"
                  onValueChange={(v) =>
                    updateShape(shape, { type: v as RegexMatch })
                  }
                />
              </div>
            </div>

            <BlockSeparator />

            {isReadOnly ? (
              <div className="flex flex-wrap gap-2">
                {shape.props.regex.map((regex, index) => (
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  <ReadOnlyValue key={`${shape.id}-${regex}-${index}`}>
                    {regex}
                  </ReadOnlyValue>
                ))}
              </div>
            ) : (
              <>
                {shape.props.regex_match ? (
                  <div className="flex flex-col gap-2">
                    {shape.props.regex.map((regex, index) => (
                      <div
                        // eslint-disable-next-line @eslint-react/no-array-index-key
                        key={`${shape.id}-list-member-${index}`}
                        className="relative group"
                      >
                        <CanvasTextArea
                          minRows={1}
                          aria-label="Word to match"
                          className="min-h-[32px] text-wrap break-all pr-10"
                          value={regex}
                          placeholder="Word to match"
                          onValueChange={(newRegex) => {
                            const regex = [...shape.props.regex];
                            regex[index] = newRegex;
                            updateShape(shape, { regex });
                          }}
                        />

                        {shape.props.regex.length > 1 && !isReadOnly && (
                          <CanvasButton
                            size="smIcon"
                            variant="ghost"
                            className="pointer-events-auto absolute right-1 top-[3px] opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
                            onPress={() => {
                              const regex = [...shape.props.regex];
                              regex.splice(index, 1);
                              updateShape(shape, { regex });
                            }}
                          >
                            <Cross1Icon />
                          </CanvasButton>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <ListWrapper>
                    {shape.props.regex.map((regex, index) => (
                      <div
                        // eslint-disable-next-line @eslint-react/no-array-index-key
                        key={`${shape.id}-list-member-${index}`}
                        className="relative group"
                      >
                        <CanvasInput
                          aria-label="Word to match"
                          value={regex}
                          placeholder="Word to match"
                          onValueChange={(newRegex) => {
                            const regex = [...shape.props.regex];
                            regex[index] = newRegex;
                            updateShape(shape, { regex });
                          }}
                          onBlur={() => {
                            const regex = [...shape.props.regex];
                            updateShape(shape, {
                              regex: Array.from(new Set(regex)),
                            });
                          }}
                        />

                        {shape.props.regex.length > 1 && !isReadOnly && (
                          <CanvasButton
                            size="smIcon"
                            variant="ghost"
                            className="pointer-events-auto absolute right-1 top-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
                            onPress={() => {
                              const regex = [...shape.props.regex];
                              regex.splice(index, 1);
                              updateShape(shape, { regex });
                            }}
                          >
                            <Cross1Icon />
                          </CanvasButton>
                        )}
                      </div>
                    ))}
                  </ListWrapper>
                )}
                <CanvasButton
                  size="sm"
                  variant="ghost"
                  onPress={() =>
                    updateShape(shape, { regex: [...shape.props.regex, ""] })
                  }
                >
                  Add Another
                  {isFocusWithin && (
                    <KbdGroup className="hidden md:flex absolute top-1/2 right-2 -translate-y-1/2">
                      <Kbd>Meta + ⏎</Kbd>
                    </KbdGroup>
                  )}
                </CanvasButton>
                <InertTermsNotice shape={shape} updateShape={updateShape} />
              </>
            )}
            <p className="text-xs text-muted-foreground">
              <span className="font-medium">{BOUNDARY_LABEL[boundary]}:</span>{" "}
              {BOUNDARY_HELP[boundary]}
            </p>
          </div>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
